/**
 * antLocale — locale code → Ant Design locale 객체 매핑 유틸.
 *
 * 【역할】
 * - main.tsx 의 ConfigProvider 에 현재 locale 용 antd Locale 을 동적으로 공급.
 * - DatePicker/Pagination/Empty/Modal/Table 등의 내부 문자열을 현지화.
 *
 * 【주요 기능】
 * - getAntdLocale(code): antd Locale 객체 (매핑 미존재 시 en_US)
 * - getDayjsLocale(code): dayjs 용 short code ('ko-KR' → 'ko')
 *
 * 【지원 언어】
 * - ko/en/zh/ja/vi — cm_locale 에 새 locale 이 추가되면 이 맵에도 import 추가 필요.
 *
 * 【사용 예시】
 * ```tsx
 * <ConfigProvider locale={getAntdLocale(i18n.language)}>
 * ```
 *
 * 【왜 이 패턴?】
 * - antd locale 은 트리 쉐이킹을 위해 명시적 import 가 필요 — 동적 require 가 아닌 빌드 타임 매핑.
 */
import type { Locale as AntdLocale } from 'antd/es/locale'
import koKR from 'antd/locale/ko_KR'
import enUS from 'antd/locale/en_US'
import zhCN from 'antd/locale/zh_CN'
import jaJP from 'antd/locale/ja_JP'
import viVN from 'antd/locale/vi_VN'

const LOCALE_MAP: Record<string, AntdLocale> = {
  ko: koKR,
  en: enUS,
  zh: zhCN,
  ja: jaJP,
  vi: viVN
}

/**
 * locale code → antd Locale 객체 변환.
 * @param code - 'ko'/'ko-KR'/'en_US' 등 다양한 형식 허용 (첫 segment 사용).
 * @returns 매핑된 antd Locale, 미존재 시 en_US.
 */
export function getAntdLocale(code: string | undefined | null): AntdLocale {
  if (!code) return enUS
  const short = code.split(/[-_]/)[0].toLowerCase()
  return LOCALE_MAP[short] ?? enUS
}

/**
 * dayjs locale short code (ko-KR → 'ko') — 다른 곳에서도 재사용.
 * @returns 소문자 short code, 미지정 시 'en'.
 */
export function getDayjsLocale(code: string | undefined | null): string {
  if (!code) return 'en'
  return code.split(/[-_]/)[0].toLowerCase()
}
